import React, { useState, useEffect } from 'react';
import { usePulsy } from 'pulsy';
import { getBorge, getLandingBoodskap } from '../services/borg_services';

// The beoordelaars' home ("Tuis") page: a thanks message and the event's sponsors, hoofborg
// first. The admin manages the content under "Borge".

const linkStyle = {
  color: '#77502d',
  textDecoration: 'none',
  fontWeight: 'bold',
  marginRight: '0.75rem',
  whiteSpace: 'nowrap'
};

const sosialie = (b) => {
  const skakels = [];
  if (b.webwerf) skakels.push(['Webwerf', b.webwerf]);
  if (b.facebook) skakels.push(['Facebook', b.facebook]);
  if (b.instagram) skakels.push(['Instagram', b.instagram]);
  if (b.x) skakels.push(['X', b.x]);
  if (b.linkedin) skakels.push(['LinkedIn', b.linkedin]);
  if (b.epos) skakels.push(['E-pos', `mailto:${b.epos}`]);
  return skakels;
};

const Logo = ({ borg, grootte }) => (
  borg.logo
    ? <img
        src={borg.logo}
        alt={borg.naam}
        style={{ maxWidth: grootte, maxHeight: grootte, width: 'auto', height: 'auto', objectFit: 'contain' }}
      />
    : <div style={{
        width: grootte, height: grootte, borderRadius: '8px', backgroundColor: '#eef1f6',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#0e1e3b', fontWeight: 'bold', fontSize: '1.5rem'
      }}>
        {borg.naam?.charAt(0)?.toUpperCase() || '?'}
      </div>
);

function Landing() {
  const [borge, setBorge] = useState([]);
  const [boodskap, setBoodskap] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [auth] = usePulsy('auth');
  const naam = auth?.user?.volle_naam || auth?.user?.username || '';

  useEffect(() => {
    Promise.all([getBorge(), getLandingBoodskap()])
      .then(([borgeData, landing]) => {
        setBorge(borgeData);
        setBoodskap(landing.boodskap || '');
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const hoofborg = borge.find(b => b.hoofborg) || null;
  const anderBorge = borge.filter(b => !b.hoofborg);

  if (loading) {
    return <div style={{ padding: '2rem', color: '#666' }}>Laai tuisblad...</div>;
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      <header style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
        <h1 style={{ color: '#0e1e3b', marginBottom: '0.5rem' }}>
          Welkom by die beoordeling{naam ? `, ${naam}` : ''}
        </h1>
        <p style={{ color: '#77502d', fontWeight: 'bold', fontSize: '1.1rem', margin: 0 }}>
          Baie dankie dat julle hierdie jaar se projekte kom beoordeel het.
        </p>
      </header>

      {boodskap && (
        <div style={{
          padding: '1.5rem', backgroundColor: '#f9f6f0', border: '1px solid #e6ddc9',
          borderRadius: '10px', marginBottom: '2.5rem', color: '#4a3a24', lineHeight: 1.6,
          whiteSpace: 'pre-wrap', textAlign: 'center'
        }}>
          {boodskap}
        </div>
      )}

      {error && (
        <div style={{
          padding: '1rem', backgroundColor: '#f8d7da', color: '#721c24',
          border: '1px solid #f5c6cb', borderRadius: '4px', marginBottom: '1.5rem'
        }}>
          Fout: {error}
        </div>
      )}

      <h2 style={{ color: '#0e1e3b', textAlign: 'center', marginBottom: '1.5rem' }}>Ons borge</h2>

      {borge.length === 0 ? (
        <p style={{ color: '#666', textAlign: 'center' }}>Daar is nog geen borge om te wys nie.</p>
      ) : (
        <>
          {hoofborg && (
            <div style={{
              padding: '2rem', border: '2px solid #77502d', borderRadius: '12px', backgroundColor: 'white',
              marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap',
              justifyContent: 'center', textAlign: 'left'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                <span style={{ color: '#77502d', fontWeight: 'bold', letterSpacing: '0.05em' }}>HOOFBORG</span>
                <Logo borg={hoofborg} grootte="160px" />
              </div>
              <div style={{ flex: '1 1 320px', minWidth: '260px', textAlign: 'left' }}>
                <h3 style={{ color: '#0e1e3b', marginTop: 0 }}>{hoofborg.naam}</h3>
                {hoofborg.bio && <p style={{ color: '#444', lineHeight: 1.6 }}>{hoofborg.bio}</p>}
                <div>
                  {sosialie(hoofborg).map(([naam, url]) => (
                    <a key={naam} href={url} target="_blank" rel="noopener noreferrer" style={linkStyle}>{naam}</a>
                  ))}
                </div>
              </div>
            </div>
          )}

          {anderBorge.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1.25rem' }}>
              {anderBorge.map(b => (
                <div key={b.borg_id} style={{
                  padding: '1.5rem', border: '1px solid #ddd', borderRadius: '10px', backgroundColor: 'white',
                  display: 'flex', flexDirection: 'column', gap: '0.75rem', textAlign: 'left'
                }}>
                  <Logo borg={b} grootte="100px" />
                  <h3 style={{ color: '#0e1e3b', margin: 0 }}>{b.naam}</h3>
                  {b.bio && <p style={{ color: '#555', margin: 0, lineHeight: 1.5, fontSize: '0.95rem' }}>{b.bio}</p>}
                  <div style={{ marginTop: 'auto' }}>
                    {sosialie(b).map(([naam, url]) => (
                      <a key={naam} href={url} target="_blank" rel="noopener noreferrer" style={{ ...linkStyle, fontSize: '0.9rem' }}>{naam}</a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Landing;
